import type { RealtimeEntityPropertyValue } from '@interfaces/realtimeEntities';

export type { RealtimeEntityPropertyValue };

export type SendMessageToUnreal = (message: unknown) => void;

export interface RealtimeEntityTransform {
  /** `[X, Y, Z]`, Unreal units. */
  position?: number[];
  /** `[Pitch, Yaw, Roll]`, degrees — FRotator constructor order, matching the hotspot wire format. */
  rotation?: number[];
  /** `[X, Y, Z]` per-axis factors; absent defaults to unit on spawn. */
  scale?: number[];
}

export interface RealtimeEntitySpawnPayload extends RealtimeEntityTransform {
  /** Wire id of the entity — without one the engine never tracks it. */
  entityId: string;
  /** A `typeId` from the build's `spawnableObjects` schema — never a class path. */
  type: string;
  properties?: Record<string, RealtimeEntityPropertyValue>;
}

export interface RealtimeEntityUpdatePayload extends RealtimeEntityTransform {
  entityId: string;
  properties?: Record<string, RealtimeEntityPropertyValue>;
}

/**
 * Keeps an update payload to exactly the fields the caller set. Presence, not
 * value, decides whether the engine applies a field — `position: [0,0,0]`
 * moves an entity to the origin while an absent `position` leaves it alone —
 * so an untouched field must never ride along as `undefined` or a default.
 *
 * @param transform - Candidate transform fields, each possibly undefined.
 * @param properties - Candidate properties object, possibly empty.
 * @returns Only the entries that are actually present (a non-empty properties object included).
 */
const pickPresentFields = (
  transform: RealtimeEntityTransform,
  properties?: Record<string, RealtimeEntityPropertyValue>,
): Record<string, unknown> => {
  return {
    ...(transform.position ? { position: transform.position } : {}),
    ...(transform.rotation ? { rotation: transform.rotation } : {}),
    ...(transform.scale ? { scale: transform.scale } : {}),
    ...(properties && Object.keys(properties).length ? { properties } : {}),
  };
};

/**
 * Reads one value as a finite `[x, y, z]` tuple, or nothing.
 *
 * @param value - Candidate transform field from an engine report.
 */
const parseFiniteTriple = (value: unknown): number[] | undefined => {
  return Array.isArray(value) &&
    value.length === 3 &&
    value.every((component) => Number.isFinite(component))
    ? (value as number[])
    : undefined;
};

/**
 * Tells whether a reported value has a shape a property can hold; `false`,
 * `0` and `''` pass.
 *
 * @param value - One entry of a reported property bag.
 * @returns Whether the value may enter the registry.
 */
const isReportedPropertyValue = (
  value: unknown,
): value is RealtimeEntityPropertyValue =>
  typeof value === 'string' ||
  typeof value === 'boolean' ||
  (typeof value === 'number' && Number.isFinite(value)) ||
  (Array.isArray(value) &&
    value.length > 0 &&
    value.every(
      (component) =>
        typeof component === 'number' && Number.isFinite(component),
    ));

/**
 * Reads the property bag of an engine report, dropping the entries the wire
 * contract forbids and keeping their siblings.
 *
 * @param raw - The `properties` field of the raw message, if any.
 * @returns The usable entries, or `undefined` when there are none.
 */
const parseReportedProperties = (
  raw: unknown,
): Record<string, RealtimeEntityPropertyValue> | undefined => {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return undefined;
  }

  const entries = Object.entries(raw as Record<string, unknown>).filter(
    ([, value]) => isReportedPropertyValue(value),
  );

  return entries.length
    ? (Object.fromEntries(entries) as Record<
        string,
        RealtimeEntityPropertyValue
      >)
    : undefined;
};

/**
 * A validated engine report: a settled gizmo drag, or the post-spawn
 * transform plus class defaults.
 */
export interface RealtimeEntityReport {
  entityId: string;
  position?: number[];
  rotation?: number[];
  scale?: number[];
  properties?: Record<string, RealtimeEntityPropertyValue>;
}

/**
 * Validates the engine's UPDATE_REALTIME_ENTITY report before anything stores
 * or renders it: only finite 3-element tuples survive, a scalar `scale` is
 * expanded per axis, and a property bag keeps only wire-shaped values.
 *
 * @param report - The raw inbound message.
 * @returns The validated report, or `null` when it carries no entity id, or
 * neither a usable transform field nor a usable property.
 */
export const parseRealtimeEntityReport = (
  report: unknown,
): RealtimeEntityReport | null => {
  const raw = report as Record<string, unknown> | null;
  const entityId = typeof raw?.entityId === 'string' ? raw.entityId : '';

  if (!entityId) {
    return null;
  }

  const rawScale = raw?.scale;
  const scale =
    typeof rawScale === 'number' && Number.isFinite(rawScale)
      ? [rawScale, rawScale, rawScale]
      : parseFiniteTriple(rawScale);
  const position = parseFiniteTriple(raw?.position);
  const rotation = parseFiniteTriple(raw?.rotation);
  const properties = parseReportedProperties(raw?.properties);

  const transform = {
    ...(position ? { position } : {}),
    ...(rotation ? { rotation } : {}),
    ...(scale ? { scale } : {}),
  };

  if (!Object.keys(transform).length && !properties) {
    return null;
  }

  return { entityId, ...transform, ...(properties ? { properties } : {}) };
};

/**
 * Tells whether a report carries at least one transform field.
 *
 * @param report - A validated report.
 * @returns Whether a transform field is present.
 */
export const reportCarriesTransform = (report: RealtimeEntityReport): boolean =>
  !!(report.position || report.rotation || report.scale);

/**
 * Wraps an angle into `[-180, 180)`, the range the engine reports rotators in.
 *
 * @param degrees - Any angle.
 * @returns The same heading, normalised.
 */
const normalizeDegrees = (degrees: number): number =>
  ((((degrees + 180) % 360) + 360) % 360) - 180;

/** What the engine spawns with when a transform field was never sent. */
const SPAWN_TRANSFORM_DEFAULTS: Record<
  keyof RealtimeEntityTransform,
  number[]
> = {
  position: [0, 0, 0],
  rotation: [0, 0, 0],
  scale: [1, 1, 1],
};

/**
 * Tells whether a reported transform is the engine echoing what the registry
 * already holds, at the float32 precision the engine holds it.
 *
 * The engine reports after its own respawn of a saved entity too: `12.34`
 * comes back as `12.340000152587890625`, yaw `270` as `-90`, a never-stored
 * field as the spawn default. Merged raw, that echo would dirty a clean row.
 *
 * @param stored - Transform the registry holds for the entity.
 * @param report - The reported transform fields.
 * @returns True when every reported field equals the stored one at float32,
 * rotations compared as headings.
 */
export const transformEchoesStored = (
  stored: RealtimeEntityTransform,
  report: RealtimeEntityTransform,
): boolean =>
  (['position', 'rotation', 'scale'] as const).every((field) => {
    const reported = report[field];

    if (!reported) {
      return true;
    }

    const held = stored[field] ?? SPAWN_TRANSFORM_DEFAULTS[field];
    const project =
      field === 'rotation'
        ? (axis: number) => Math.fround(normalizeDegrees(axis))
        : (axis: number) => Math.fround(axis);

    return (
      held.length === reported.length &&
      reported.every((axis, index) => project(axis) === project(held[index]))
    );
  });

/**
 * Spawns one or more real-time entities in the author's engine instance.
 *
 * Every entity is sent with an `entityId`: the engine tracks entities only by
 * the id they were spawned with, so an entity spawned without one could never
 * be edited or deleted afterwards.
 *
 * @param sendMessageToUnreal - Bridge that forwards the message to the stream.
 * @param entities - Entities to create; absent position/rotation default to
 * origin/zero and absent scale to unit, engine-side.
 */
export const spawnRealtimeEntitiesInUnreal = (
  sendMessageToUnreal: SendMessageToUnreal,
  entities: RealtimeEntitySpawnPayload[],
) => {
  sendMessageToUnreal({
    action: 'SPAWN_REALTIME_ENTITIES',
    entities: entities.map((entity) => ({
      entityId: entity.entityId,
      type: entity.type,
      ...pickPresentFields(entity, entity.properties),
    })),
  });
};

/**
 * Moves and/or re-configures one spawned entity. One entity per call — the
 * action is not a batch.
 *
 * A `properties` object writes only the keys it carries, so editing one field
 * cannot reset the rest to defaults.
 *
 * @param sendMessageToUnreal - Bridge that forwards the message to the stream.
 * @param update - The entity id plus only the fields that actually changed.
 */
export const updateRealtimeEntityInUnreal = (
  sendMessageToUnreal: SendMessageToUnreal,
  update: RealtimeEntityUpdatePayload,
) => {
  sendMessageToUnreal({
    action: 'UPDATE_REALTIME_ENTITY',
    entityId: update.entityId,
    ...pickPresentFields(update, update.properties),
  });
};

/**
 * Removes one spawned entity. The engine reads `entityId` and nothing else,
 * and refuses an absent or empty id rather than reading it as "delete
 * everything" — this guard mirrors that so a bug here cannot even ask.
 *
 * @param sendMessageToUnreal - Bridge that forwards the message to the stream.
 * @param entityId - Entity to remove; its id is free for reuse afterwards.
 */
export const deleteRealtimeEntityInUnreal = (
  sendMessageToUnreal: SendMessageToUnreal,
  entityId: string,
) => {
  if (!entityId) {
    return;
  }

  sendMessageToUnreal({
    action: 'DELETE_REALTIME_ENTITY',
    entityId,
  });
};

/**
 * Opens the in-world transform gizmo on one spawned entity.
 *
 * Sending a start for a different id while one entity is already being edited
 * switches the gizmo over — no explicit end is required first.
 *
 * @param sendMessageToUnreal - Bridge that forwards the message to the stream.
 * @param entityId - Entity to attach the gizmo to.
 */
export const startRealtimeEntityEditInUnreal = (
  sendMessageToUnreal: SendMessageToUnreal,
  entityId: string,
) => {
  sendMessageToUnreal({
    action: 'START_REALTIME_ENTITY_EDIT',
    entityId,
  });
};

/**
 * Closes the in-world transform gizmo.
 *
 * @param sendMessageToUnreal - Bridge that forwards the message to the stream.
 * @param entityId - Entity to end the edit for; the engine ends whatever is
 * selected when this is omitted, and ignores an id naming a different entity
 * than the selected one (a stale race).
 */
export const endRealtimeEntityEditInUnreal = (
  sendMessageToUnreal: SendMessageToUnreal,
  entityId?: string,
) => {
  sendMessageToUnreal({
    action: 'END_REALTIME_ENTITY_EDIT',
    ...(entityId ? { entityId } : {}),
  });
};
